import type { ChipProps } from '@mui/material/Chip';

export type UiChipStyle = {
  color: ChipProps['color'];
  variant: ChipProps['variant'];
};

export type UiMeta = {
  label: string;
  description?: string;
  chip: UiChipStyle;
  order?: number;
};

export type UiOption<T extends string> = {
  value: T;
  label: string;
};

export const DEFAULT_CHIP_STYLE: UiChipStyle = {
  color: 'default',
  variant: 'outlined',
};

export const getFallbackUiMeta = (
  value: unknown,
  fallbackLabel = '-',
): UiMeta => ({
  label: value == null || value === '' ? fallbackLabel : String(value),
  chip: DEFAULT_CHIP_STYLE,
});

export const getUiMeta = <T extends string>(
  meta: Readonly<Record<T, UiMeta>>,
  value?: T | string | null,
  fallbackLabel = '-',
): UiMeta => {
  if (!value) return getFallbackUiMeta(value, fallbackLabel);
  return meta[value as T] ?? getFallbackUiMeta(value, fallbackLabel);
};

export const getUiLabel = <T extends string>(
  meta: Readonly<Record<T, UiMeta>>,
  value?: T | string | null,
  fallbackLabel = '-',
): string => getUiMeta(meta, value, fallbackLabel).label;

export const toLabelMap = <T extends string>(
  meta: Readonly<Record<T, UiMeta>>,
): Record<T, string> =>
  (Object.keys(meta) as T[]).reduce(
    (acc, key) => {
      acc[key] = meta[key].label;
      return acc;
    },
    {} as Record<T, string>,
  );

export const toUiOptions = <T extends string>(
  meta: Readonly<Record<T, UiMeta>>,
): UiOption<T>[] =>
  (Object.keys(meta) as T[])
    .sort((a, b) => {
      const orderDiff =
        (meta[a].order ?? Number.MAX_SAFE_INTEGER) -
        (meta[b].order ?? Number.MAX_SAFE_INTEGER);
      return orderDiff || meta[a].label.localeCompare(meta[b].label);
    })
    .map((value) => ({
      value,
      label: meta[value].label,
    }));
